import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api';

export const NOTIFICACOES_PREFIX = 'notificacoes';
export const RESUMO_QUERY_KEY = [NOTIFICACOES_PREFIX, 'resumo'];
export const PREFERENCIAS_QUERY_KEY = [NOTIFICACOES_PREFIX, 'preferencias'];
export const ESTADO_QUERY_KEY = [NOTIFICACOES_PREFIX, 'estado'];
export const HISTORICO_QUERY_KEY = [NOTIFICACOES_PREFIX, 'historico'];

export const NOTIFICACOES_RESUMO_INICIAL = Object.freeze({
  total: 0,
  lidas: 0,
  naoLidas: 0,
  porCategoria: {},
});

export const HISTORICO_RESUMO_INICIAL = Object.freeze({
  total: 0,
  cliente: 0,
  admin: 0,
  porCategoria: {},
});

/** Espelha `backend/notifications/estadoOpcoes.js`. */
export const NOTIFICACOES_ABAS = Object.freeze(['todas', 'nao-lidas', 'lidas']);
export const NOTIFICACOES_CATEGORIAS = Object.freeze([
  'pedido',
  'pagamento',
  'arquivo',
  'selecao',
  'sistema',
  'conta',
  'outro',
]);
export const NOTIFICACOES_ITENS_POR_PAGINA = Object.freeze([10, 20, 30, 50]);

export const NOTIFICACOES_ESTADO_INICIAL = Object.freeze({
  aba: 'todas',
  categoria: null,
  itensPorPagina: 20,
  atualizadoEm: null,
});

/** Aba -> filtro `lida` da API (`undefined` = todas). */
export const LIDA_POR_ABA = Object.freeze({
  todas: undefined,
  'nao-lidas': false,
  lidas: true,
});

export const notificacoesQueryKey = (filters = {}) => [NOTIFICACOES_PREFIX, 'list', filters];

/** Invalida tudo que é alimentado por notificações (lista + contador + estado). */
export const invalidateNotificacoes = (queryClient) => {
  if (!queryClient) return;
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey[0] === NOTIFICACOES_PREFIX,
  });
};

/** Normaliza o estado vindo do backend (nunca deixa a UI com valor inválido). */
export const normalizarEstado = (data = {}) => {
  const itens = Number(data.itensPorPagina);

  return {
    aba: NOTIFICACOES_ABAS.includes(data.aba) ? data.aba : NOTIFICACOES_ESTADO_INICIAL.aba,
    categoria:
      typeof data.categoria === 'string' && NOTIFICACOES_CATEGORIAS.includes(data.categoria)
        ? data.categoria
        : null,
    itensPorPagina: NOTIFICACOES_ITENS_POR_PAGINA.includes(itens)
      ? itens
      : NOTIFICACOES_ESTADO_INICIAL.itensPorPagina,
    atualizadoEm: data.atualizadoEm || null,
  };
};

const fetchResumo = () =>
  api.get('/notificacoes/resumo').then((response) => ({
    ...NOTIFICACOES_RESUMO_INICIAL,
    ...(response.data || {}),
  }));

const fetchNotificacoes = (filters) =>
  api.get('/notificacoes', { params: filters }).then((response) => response.data);

const fetchPreferencias = () =>
  api.get('/notificacoes/preferencias').then((response) => response.data);

const fetchEstado = () =>
  api.get('/notificacoes/estado').then((response) => normalizarEstado(response.data));

const fetchHistorico = (filters) =>
  api.get('/notificacoes/historico', { params: filters }).then((response) => response.data);

/** Resumo usado pelo sino, abas e página (total, lidas, não lidas e categorias). */
export const useNotificacoesResumo = () => {
  const query = useQuery({
    queryKey: RESUMO_QUERY_KEY,
    queryFn: fetchResumo,
    staleTime: 15000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Rede de segurança para dispositivos sem socket: sem isso o badge só
    // mudaria ao recarregar a página.
    refetchInterval: 60000,
  });

  return {
    resumo: { ...NOTIFICACOES_RESUMO_INICIAL, ...(query.data || {}) },
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};

/**
 * Estado da área de notificações gravado no backend por usuário.
 *
 * A aba, a categoria e os itens por página são lidos do servidor (e não do
 * navegador), então a gaveta do sino, a central e todos os dispositivos abrem
 * na mesma janela. `salvar` é otimista e só sai de handlers de interação —
 * eventos de tempo real atualizam o cache, sem loop entre dispositivos.
 */
export const useEstadoNotificacoes = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ESTADO_QUERY_KEY,
    queryFn: fetchEstado,
    staleTime: 60000,
    // Outra aba/dispositivo pode ter trocado de janela: revalida ao voltar o foco
    // (o evento de tempo real cobre o caso de as duas abas estarem visíveis).
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const salvar = useMutation({
    mutationFn: (updates) =>
      api.put('/notificacoes/estado', updates).then((response) => normalizarEstado(response.data)),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ESTADO_QUERY_KEY });
      const anterior = queryClient.getQueryData(ESTADO_QUERY_KEY);

      queryClient.setQueryData(ESTADO_QUERY_KEY, (atual) =>
        normalizarEstado({ ...NOTIFICACOES_ESTADO_INICIAL, ...(atual || {}), ...updates })
      );

      return { anterior };
    },
    onError: (_error, _updates, context) => {
      if (context?.anterior !== undefined) {
        queryClient.setQueryData(ESTADO_QUERY_KEY, context.anterior);
      }
    },
    onSuccess: (estado) => {
      queryClient.setQueryData(ESTADO_QUERY_KEY, estado);
    },
  });

  return {
    estado: normalizarEstado({ ...NOTIFICACOES_ESTADO_INICIAL, ...(query.data || {}) }),
    isLoading: query.isLoading,
    refetch: query.refetch,
    salvar,
  };
};

/** Contador usado pelo sino. Mantém compatibilidade com o hook anterior. */
export const useContadorNotificacoes = () => {
  const { resumo, isLoading, refetch } = useNotificacoesResumo();

  return {
    naoLidas: resumo.naoLidas,
    total: resumo.total,
    lidas: resumo.lidas,
    resumo,
    isLoading,
    refetch,
  };
};

const normalizarLeitura = (input) => {
  const id = typeof input === 'string' ? input : input?.id;
  const lida = typeof input === 'object' && input?.lida !== undefined ? input.lida : true;
  return { id, lida: Boolean(lida) };
};

const ajustarResumo = (resumo, notificacao, lida) => {
  if (!resumo || !notificacao || Boolean(notificacao.lida) === lida) return resumo;

  const delta = lida ? 1 : -1;
  const categoria = notificacao.categoria || 'outro';
  const atual = resumo.porCategoria?.[categoria] || { total: 0, lidas: 0, naoLidas: 0 };
  const porCategoria = {
    ...(resumo.porCategoria || {}),
    [categoria]: {
      ...atual,
      lidas: Math.max(0, (atual.lidas || 0) + delta),
      naoLidas: Math.max(0, (atual.naoLidas || 0) - delta),
    },
  };

  return {
    ...resumo,
    lidas: Math.max(0, (resumo.lidas || 0) + delta),
    naoLidas: Math.max(0, (resumo.naoLidas || 0) - delta),
    porCategoria,
  };
};

/**
 * Lista + mutações do drawer e da central.
 *
 * @param {{lida?: boolean, categoria?: string, busca?: string, page?: number, limit?: number, enabled?: boolean}} options
 */
export const useNotificacoes = ({
  lida,
  categoria,
  busca,
  page = 1,
  limit = 30,
  enabled = true,
} = {}) => {
  const queryClient = useQueryClient();
  const termo = typeof busca === 'string' ? busca.trim() : '';
  const filters = {
    page,
    limit,
    ...(typeof lida === 'boolean' ? { lida } : {}),
    ...(categoria ? { categoria } : {}),
    ...(termo ? { busca: termo } : {}),
  };

  const query = useQuery({
    queryKey: notificacoesQueryKey(filters),
    queryFn: () => fetchNotificacoes(filters),
    staleTime: 15000,
    // Aba em segundo plano/dispositivo voltando ao foco: relê o estado real do
    // servidor em vez de manter a lista congelada.
    refetchOnWindowFocus: true,
    enabled,
  });

  const onSuccess = () => invalidateNotificacoes(queryClient);

  const marcarLida = useMutation({
    mutationFn: (input) => {
      const { id, lida: novaLeitura } = normalizarLeitura(input);
      return api.patch(`/notificacoes/${id}/lida`, { lida: novaLeitura });
    },
    onMutate: async (input) => {
      const { id, lida: novaLeitura } = normalizarLeitura(input);
      const predicate = (item) =>
        Array.isArray(item.queryKey) && item.queryKey[0] === NOTIFICACOES_PREFIX;

      await queryClient.cancelQueries({ predicate });
      const previousLists = queryClient.getQueriesData({ predicate });
      const previousResumo = queryClient.getQueryData(RESUMO_QUERY_KEY);
      let notificacao = null;

      for (const [key, data] of previousLists) {
        if (!data?.notificacoes) continue;

        const alvo = data.notificacoes.find((item) => item.id === id);
        if (!alvo) continue;

        notificacao = alvo;
        queryClient.setQueryData(key, {
          ...data,
          notificacoes: data.notificacoes.map((item) =>
            item.id === id ? { ...item, lida: novaLeitura } : item
          ),
        });
      }

      if (notificacao) {
        queryClient.setQueryData(RESUMO_QUERY_KEY, (atual) =>
          ajustarResumo(atual, notificacao, novaLeitura)
        );
      }

      return { previousLists, previousResumo, notificacao };
    },
    onError: (_error, _input, context) => {
      context?.previousLists?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      if (context?.previousResumo !== undefined) {
        queryClient.setQueryData(RESUMO_QUERY_KEY, context.previousResumo);
      }
    },
    onSettled: onSuccess,
  });

  const marcarTodas = useMutation({
    mutationFn: () => api.patch('/notificacoes/marcar-todas-lidas'),
    onSuccess,
  });

  const limparLidas = useMutation({
    mutationFn: () => api.delete('/notificacoes/lidas'),
    onSuccess,
  });

  const remover = useMutation({
    mutationFn: (id) => api.delete(`/notificacoes/${id}`),
    onSuccess,
  });

  return {
    notificacoes: query.data?.notificacoes || [],
    pagination: query.data?.pagination || { total: 0, page, limit, pages: 1 },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    marcarLida,
    marcarTodas,
    limparLidas,
    remover,
  };
};

/**
 * Histórico administrativo de envios (clientes + admins).
 *
 * É append-only no backend: marcar como lida, limpar lidas ou remover no sino
 * não altera esta listagem. Filtros e paginação são locais (não passam pelo
 * `NotificacaoEstado`, que é compartilhado com a gaveta do sino).
 *
 * @param {{destinatarioTipo?: string, categoria?: string, busca?: string, page?: number, limit?: number, enabled?: boolean}} options
 */
export const useHistoricoNotificacoes = ({
  destinatarioTipo,
  categoria,
  busca,
  page = 1,
  limit = 20,
  enabled = true,
} = {}) => {
  const termo = typeof busca === 'string' ? busca.trim() : '';
  const filters = {
    page,
    limit,
    ...(destinatarioTipo ? { destinatarioTipo } : {}),
    ...(categoria ? { categoria } : {}),
    ...(termo ? { busca: termo } : {}),
  };

  const query = useQuery({
    queryKey: [...HISTORICO_QUERY_KEY, 'list', filters],
    queryFn: () => fetchHistorico(filters),
    staleTime: 15000,
    refetchOnWindowFocus: true,
    enabled,
  });

  return {
    envios: query.data?.envios || [],
    pagination: query.data?.pagination || { total: 0, page, limit, pages: 1 },
    resumo: { ...HISTORICO_RESUMO_INICIAL, ...(query.data?.resumo || {}) },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

/** Preferências de e-mail por categoria. */
export const usePreferenciasNotificacoes = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PREFERENCIAS_QUERY_KEY,
    queryFn: fetchPreferencias,
    staleTime: 60000,
  });

  const atualizar = useMutation({
    mutationFn: (updates) =>
      api.put('/notificacoes/preferencias', updates).then((response) => response.data),
    onSuccess: (data) => {
      queryClient.setQueryData(PREFERENCIAS_QUERY_KEY, data);
    },
  });

  return {
    preferencias: query.data || { emailAtivo: true, pushAtivo: false, categoriasDesativadas: [] },
    isLoading: query.isLoading,
    atualizar,
  };
};
